import { useApolloClient } from "@apollo/client";
import { IconName, Intent, Tree, TreeNodeInfo } from "@blueprintjs/core";
import {
  CREATE_DATA_CONNECTION_HEX_VERSION_LINK,
  CellType,
  ColumnSemanticLayerType,
  DataBrowserTab,
  DataConnectionHexVersionLinkId,
  DataConnectionId,
  DataConnectionType,
  DataSourceColumnId,
  DataSourceTableConfig,
  DataSourceTableId,
  DbtTestMetadata,
  NoCodeCellType,
  TableType,
  assertNever,
  buildLimitedQuery,
  constructTableSearchQuery,
  createSqlCellPayload,
  generateDataframeNameForDataSourceTable,
  generateSelectColumnsSQL,
  generateTableNameSQL,
  getTablePath as getTablePathUtil,
  stableEmptyArray,
  uuid,
  webUrlFromAccessUrl,
} from "@hex/common";
import { range } from "lodash";
import React, { useCallback } from "react";
import { Literal, Static, Union } from "runtypes";

import { useGetNewSqlCellFields } from "../../../hooks/cell/useGetNewCellFields";
import {
  LocalStorageKeys,
  useLocalStorageSetter,
} from "../../../hooks/useLocalStorage";
import {
  SessionStorageKeys,
  useSessionStorageSetter,
} from "../../../hooks/useSessionStorage.js";
import { useDispatch, useSelector } from "../../../redux/hooks";
import { CellLocation } from "@hex/common";
import {
  NodeMetadata,
  NodePath,
  SelectedNodeInfo,
  TreeType,
  nodePathFromNodeIdPath,
  setNodeSelected,
  setSearchActive,
  setSearchInput,
} from "../../../redux/slices/schemaTreeBrowserSlice";
import { AppDispatch } from "../../../redux/store.js";
import { useHexVersionAOContext } from "../../../util/hexVersionAOContext";
import { Keys, evtModKey } from "../../../util/Keys";
import { useHexFlag } from "../../../util/useHexFlags";
import { useToaster } from "../../common/Toasts";
import { TableListFragment } from "../../sidebar/data-browser/DataBrowserTableList.generated.js";
import { PinnedSchemaFragment } from "../../sidebar/data-browser/FavoritesView.generated.js";

import {
  DataSourceColumnResultFragment,
  DataSourceTableByIdDocument,
  DataSourceTableByIdQuery,
  DataSourceTableByIdQueryVariables,
} from "./queries.generated";
import { useCreateNoCodeCell } from "./useCreateNoCodeCell.js";

export const AllowedNavKeys = Union(
  Literal(Keys.ARROW_UP),
  Literal(Keys.ARROW_DOWN),
  Literal(Keys.ARROW_LEFT),
  Literal(Keys.ARROW_RIGHT),
  Literal(Keys.ENTER),
);
export const AllowedColumnNavKeys = Union(
  Literal(Keys.ARROW_UP),
  Literal(Keys.ARROW_DOWN),
  Literal(Keys.ENTER),
);
export type AllowedNavKeys = Static<typeof AllowedNavKeys>;
export type AllowedColumnNavKeys = Static<typeof AllowedColumnNavKeys>;

interface GetDbtMetadataDocsArgs {
  accessUrl: string | null;
  accountId: number | string | null;
  projectId: string | null;
  runId: string | null;
  uniqueId: string | null;
}

export interface DbtMetadataLinks {
  docs: string;
  run: string;
}

export const getDbtMetadataLinks = ({
  accessUrl,
  accountId,
  projectId,
  runId,
  uniqueId,
}: GetDbtMetadataDocsArgs): DbtMetadataLinks | null => {
  const resolvedAccessUrl = webUrlFromAccessUrl(
    accessUrl ?? "cloud.getdbt.com",
  );

  if (
    accountId == null ||
    projectId == null ||
    runId == null ||
    uniqueId == null
  ) {
    return null;
  }

  // `uniqueId` amounts to `model.*` or `source.*` in general, but can support other types as well.
  const resolvedType = uniqueId.split(".")[0];

  // Go to cloud.getdbt.com and verify these links are valid via dbt's UI
  return {
    docs: `https://${resolvedAccessUrl}/accounts/${accountId}/runs/${runId}/docs/#!/${resolvedType}/${uniqueId}`,
    run: `https://${resolvedAccessUrl}/deploy/${accountId}/projects/${projectId}/runs/${runId}`,
  };
};

export const getColumnDbtTestInfo = (column: {
  name: string;
  dbtTests: readonly DbtTestMetadata[] | null;
}): { color: Intent; icon: IconName } | undefined => {
  if (column.dbtTests && column.dbtTests.length > 0) {
    let icon: IconName = "blank";
    let color: Intent = Intent.NONE;
    if (column.dbtTests?.some((test) => test.state === "error")) {
      icon = "error";
      color = Intent.DANGER;
    } else if (column.dbtTests?.some((test) => test.state === "fail")) {
      icon = "cross";
      color = Intent.DANGER;
    } else if (column.dbtTests?.some((test) => test.state === "warn")) {
      icon = "warning-sign";
      color = Intent.WARNING;
    } else if (column.dbtTests?.some((test) => test.state === "pass")) {
      icon = "tick";
      color = Intent.SUCCESS;
    }

    return { color, icon };
  }
};

// When the tree is filtered to only pinned nodes and their children, we need to
// to translate a node path on the filtered tree to a node path on the full tree.
export const translateFilteredNodePath = (
  filteredNodePath: NodePath,
  nodes: TreeNodeInfo<NodeMetadata>[],
  filteredNodes: TreeNodeInfo<NodeMetadata>[],
): NodePath => {
  const resolvedNodePath = nodePathFromNodeIdPath(
    nodes,
    range(0, filteredNodePath.length).map((idx) => {
      return Tree.nodeFromPath(
        filteredNodePath.slice(0, idx + 1),
        filteredNodes,
      ).id;
    }),
  );
  if (resolvedNodePath) {
    return resolvedNodePath;
  }
  console.error("Unable to resolve node path", filteredNodePath);
  return filteredNodePath;
};

export const computeNextNode = ({
  allowMoveToChild = true,
  browserNodes,
  direction,
  multiDatabase,
  nodes,
  pinnedOnly,
  selectedNode,
  selectedNodePath,
}: {
  selectedNode: TreeNodeInfo<NodeMetadata>;
  selectedNodePath: NodePath;
  nodes: TreeNodeInfo<NodeMetadata>[];
  browserNodes?: TreeNodeInfo<NodeMetadata>[];
  allowMoveToChild?: boolean;
  direction: "up" | "down";
  multiDatabase: boolean;
  pinnedOnly: boolean;
}):
  | {
      path: NodePath;
      nodeId: string | number;
    }
  | undefined => {
  const tablePathLength = multiDatabase ? 3 : 2;
  let newPath: NodePath | undefined = undefined;
  let nextNode: TreeNodeInfo<NodeMetadata> | undefined = undefined;
  // If moving down from an expanded node, go to its first child
  if (selectedNode.isExpanded && allowMoveToChild && direction === "down") {
    newPath = [...selectedNodePath, 0];
    // Otherwise increment the deepest node index by one in the appropriate direction
  } else {
    newPath = [
      ...selectedNodePath.slice(0, -1),
      selectedNodePath[selectedNodePath.length - 1] +
        (direction === "down" ? 1 : -1),
    ];
  }
  // if newPath is out of bounds, this returns `undefined`
  nextNode = Tree.nodeFromPath(newPath, nodes);
  // if moving up would take us to an expanded node, go to its last child
  if (
    nextNode &&
    direction === "up" &&
    nextNode.isExpanded &&
    nextNode.childNodes
  ) {
    newPath = [...newPath, nextNode.childNodes.length - 1];
    nextNode = Tree.nodeFromPath(newPath, nodes);
    // If the last child is itself an expanded node, go to its last child
    if (nextNode && nextNode.childNodes && nextNode.isExpanded) {
      newPath = [...newPath, nextNode.childNodes.length - 1];
      nextNode = Tree.nodeFromPath(newPath, nodes);
    }
  }

  // If we get here, it means there's no next node at the current level
  if (!nextNode) {
    // this means we're at the first root node, so there's not possible next node
    if (selectedNodePath.length === 1) {
      return undefined;
      // if going up, just go to the node's parent
    } else if (direction === "up") {
      newPath = newPath.slice(0, -1);
      nextNode = Tree.nodeFromPath(newPath, nodes);
      // if going down recursively determine the next node after this node's parent, without
      // moving to a child node
    } else {
      return computeNextNode({
        nodes,
        selectedNodePath: selectedNodePath.slice(0, -1),
        selectedNode,
        allowMoveToChild: false,
        direction: "down",
        pinnedOnly,
        multiDatabase,
        browserNodes,
      });
    }
  }
  // If we're looking only at pinned nodes, and we've navigated to an unpinned database
  // or schema, skip and determine the next node
  if (nextNode && pinnedOnly && newPath.length < tablePathLength) {
    let nextNodePinned = nextNode.nodeData?.pinned;
    // If navigating the search results tree, we need to find the corresponding browser
    // node to determine if it's pinned
    if (browserNodes) {
      let nextBrowserNode: TreeNodeInfo<NodeMetadata> | undefined = undefined;
      if (newPath.length === 1) {
        nextBrowserNode = browserNodes.find((n) => n.id === nextNode?.id);
      } else if (newPath.length === 2) {
        const [dbIdx, schemaIdx] = newPath;
        nextBrowserNode = browserNodes
          .find((n) => n.id === nodes[dbIdx].id)
          ?.childNodes?.find(
            (n) => n.id === nodes[dbIdx].childNodes?.[schemaIdx].id,
          );
      }
      nextNodePinned = nextBrowserNode?.nodeData?.pinned;
    }
    if (!nextNodePinned) {
      return computeNextNode({
        nodes,
        selectedNodePath: newPath,
        selectedNode: nextNode,
        allowMoveToChild: false,
        direction,
        multiDatabase,
        pinnedOnly,
        browserNodes,
      });
    }
  }
  return nextNode ? { path: newPath, nodeId: nextNode.id } : undefined;
};

interface UseHandleColumnSearchResultKeyboardEventArgs {
  readonly searchResults: DataSourceColumnResultFragment[];
  readonly dataConnectionId: DataConnectionId;
  readonly handleResultClick: ({
    columnId,
    idx,
    tableId,
    togglePanel,
  }: {
    columnId: DataSourceColumnId;
    togglePanel: boolean;
    tableId: DataSourceTableId;
    idx: number;
  }) => void;
}

export function useHandleColumnSearchResultKeyboardEvent({
  dataConnectionId,
  handleResultClick,
  searchResults,
}: UseHandleColumnSearchResultKeyboardEventArgs): (
  evt: React.KeyboardEvent,
) => void {
  const selectedColumnSearchResult = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]
        ?.selectedColumnSearchResult,
  );
  return useCallback(
    (evt: React.KeyboardEvent) => {
      const key = evt.key;
      if (searchResults.length === 0) return;
      if (evt.defaultPrevented) return;
      if (AllowedColumnNavKeys.guard(key)) {
        // prevent propagating to the global key handler for navigating cells
        evt.stopPropagation();
        evt.preventDefault();
        if (
          (key === Keys.ARROW_UP || key === Keys.ARROW_DOWN) &&
          !selectedColumnSearchResult
        ) {
          const nextIdx = key === Keys.ARROW_UP ? searchResults.length - 1 : 0;
          const next = searchResults[nextIdx];
          handleResultClick({
            tableId: next.dataSourceTable.id,
            columnId: next.id,
            togglePanel: false,
            idx: nextIdx,
          });
        } else if (selectedColumnSearchResult) {
          switch (key) {
            case Keys.ARROW_DOWN: {
              const nextIdx =
                searchResults.findIndex(
                  (col) => col.id === selectedColumnSearchResult,
                ) + 1;
              if (nextIdx < searchResults.length) {
                const next = searchResults[nextIdx];
                handleResultClick({
                  tableId: next.dataSourceTable.id,
                  columnId: next.id,
                  togglePanel: false,
                  idx: nextIdx,
                });
              }
              break;
            }
            case Keys.ARROW_UP: {
              const nextIdx =
                searchResults.findIndex(
                  (col) => col.id === selectedColumnSearchResult,
                ) - 1;
              if (nextIdx >= 0) {
                const next = searchResults[nextIdx];
                handleResultClick({
                  tableId: next.dataSourceTable.id,
                  columnId: next.id,
                  togglePanel: false,
                  idx: nextIdx,
                });
              }
              break;
            }
            case Keys.ENTER: {
              const currentIdx = searchResults.findIndex(
                (col) => col.id === selectedColumnSearchResult,
              );
              const current = searchResults[currentIdx];
              if (current) {
                handleResultClick({
                  tableId: current.dataSourceTable.id,
                  columnId: current.id,
                  togglePanel: true,
                  idx: currentIdx,
                });
              }
              break;
            }
            default: {
              assertNever(key, key);
            }
          }
        }
      }
    },
    [handleResultClick, searchResults, selectedColumnSearchResult],
  );
}

interface UseHomePageHandleSchemaBrowserKeyboardEventArgs {
  dataConnectionId: DataConnectionId;
  treeType: TreeType;
  handleNodeExpand: (node: TreeNodeInfo<NodeMetadata>, path: NodePath) => void;
  handleNodeCollapse: (
    node: TreeNodeInfo<NodeMetadata>,
    path: NodePath,
  ) => void;
  handleNodeClick: (node: TreeNodeInfo<NodeMetadata>, path: NodePath) => void;
  multiDatabase: boolean;
  pinnedOnly: boolean;
}

type UseProjectHandleSchemaBrowserKeyboardEventArgs =
  UseHomePageHandleSchemaBrowserKeyboardEventArgs & {
    connectionImported: boolean;
    connectionType: DataConnectionType;
  };

export function handleSharedSchemaTreeKeyboardEvent({
  browserNodes,
  dataConnectionId,
  dispatch,
  evt,
  handleNodeClick,
  handleNodeCollapse,
  handleNodeExpand,
  multiDatabase,
  nodes,
  pinnedOnly,
  selectedNode,
  selectedNodeInfo,
  treeType,
}: {
  nodes: TreeNodeInfo<NodeMetadata>[];
  browserNodes: TreeNodeInfo<NodeMetadata>[];
  selectedNode: TreeNodeInfo<NodeMetadata> | undefined;
  selectedNodeInfo: SelectedNodeInfo | undefined;
  dataConnectionId: DataConnectionId;
  treeType: TreeType;
  dispatch: AppDispatch;
  multiDatabase: boolean;
  pinnedOnly: boolean;
  evt: React.KeyboardEvent;
  handleNodeExpand: (node: TreeNodeInfo<NodeMetadata>, path: NodePath) => void;
  handleNodeCollapse: (
    node: TreeNodeInfo<NodeMetadata>,
    path: NodePath,
  ) => void;
  handleNodeClick: (node: TreeNodeInfo<NodeMetadata>, path: NodePath) => void;
}): void {
  const tablePathLength = multiDatabase ? 3 : 2;
  const key = evt.key;

  if (AllowedNavKeys.guard(key)) {
    // prevent propagating to the global key handler for navigating cells
    evt.stopPropagation();
    evt.preventDefault();
    if (
      (key === Keys.ARROW_UP || key === Keys.ARROW_DOWN) &&
      !selectedNodeInfo?.path
    ) {
      dispatch(
        setNodeSelected({
          dataConnectionId,
          nodeId:
            key === Keys.ARROW_UP ? nodes[nodes.length - 1].id : nodes[0].id,
          isSelected: true,
          treeType,
          nodeType: "object",
          path: key === Keys.ARROW_UP ? [nodes.length - 1] : [0],
        }),
      );
    } else if (selectedNodeInfo?.path && selectedNode) {
      switch (key) {
        case Keys.ARROW_DOWN: {
          const nextNodeInfo = computeNextNode({
            nodes,
            selectedNode,
            selectedNodePath: selectedNodeInfo.path,
            direction: "down",
            pinnedOnly,
            multiDatabase,
            browserNodes: treeType === "search" ? browserNodes : undefined,
          });
          if (nextNodeInfo) {
            dispatch(
              setNodeSelected({
                dataConnectionId,
                nodeId: nextNodeInfo.nodeId,
                isSelected: true,
                treeType,
                nodeType:
                  nextNodeInfo.path.length === tablePathLength
                    ? "table"
                    : "object",
                path: nextNodeInfo.path,
              }),
            );
          }
          break;
        }
        case Keys.ARROW_UP: {
          const nextNodeInfo = computeNextNode({
            nodes,
            selectedNode,
            selectedNodePath: selectedNodeInfo.path,
            direction: "up",
            pinnedOnly,
            multiDatabase,
            browserNodes: treeType === "search" ? browserNodes : undefined,
          });
          if (nextNodeInfo) {
            dispatch(
              setNodeSelected({
                dataConnectionId,
                nodeId: nextNodeInfo.nodeId,
                isSelected: true,
                treeType,
                nodeType:
                  nextNodeInfo.path.length === tablePathLength
                    ? "table"
                    : "object",
                path: nextNodeInfo.path,
              }),
            );
          }
          break;
        }
        case Keys.ARROW_RIGHT: {
          handleNodeExpand(selectedNode, selectedNodeInfo.path);
          break;
        }
        case Keys.ARROW_LEFT: {
          if (selectedNode && selectedNodeInfo && selectedNode.isExpanded) {
            handleNodeCollapse(selectedNode, selectedNodeInfo.path);
          } else if (selectedNodeInfo.path.length > 1) {
            const parentPath = selectedNodeInfo.path.slice(0, -1);
            const parentNode = Tree.nodeFromPath(parentPath, nodes);
            handleNodeCollapse(parentNode, parentPath);
            dispatch(
              setNodeSelected({
                dataConnectionId,
                nodeId: parentNode.id,
                isSelected: true,
                treeType,
                nodeType: "object",
                path: parentPath,
              }),
            );
          }
          break;
        }
        case Keys.ENTER: {
          handleNodeClick(selectedNode, selectedNodeInfo.path);
          break;
        }
        default: {
          assertNever(key, key);
        }
      }
    }
  }
}
export function useHomePageHandleSchemaTreeKeyboardEvent({
  dataConnectionId,
  handleNodeClick,
  handleNodeCollapse,
  handleNodeExpand,
  multiDatabase,
  pinnedOnly,
  treeType,
}: UseHomePageHandleSchemaBrowserKeyboardEventArgs) {
  const browserNodes = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]?.browserNodes ??
      stableEmptyArray(),
  );
  const selectedBrowserNodeInfo = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]
        ?.selectedBrowserNode,
  );
  const searchNodes = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]?.searchNodes ??
      stableEmptyArray(),
  );
  const selectedSearchNodeInfo = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]
        ?.selectedSearchNode,
  );
  const selectedNodeInfo =
    treeType === "browser" ? selectedBrowserNodeInfo : selectedSearchNodeInfo;
  const nodes = treeType === "browser" ? browserNodes : searchNodes;
  const selectedNode =
    selectedNodeInfo?.path && nodes.length > 0
      ? Tree.nodeFromPath(selectedNodeInfo.path, nodes)
      : undefined;
  const dispatch = useDispatch();

  return useCallback(
    (evt: React.KeyboardEvent) => {
      if (nodes.length === 0) return;
      if (evt.defaultPrevented) return;
      handleSharedSchemaTreeKeyboardEvent({
        browserNodes,
        dataConnectionId,
        dispatch,
        evt,
        handleNodeClick,
        handleNodeCollapse,
        handleNodeExpand,
        multiDatabase,
        nodes,
        pinnedOnly,
        selectedNode,
        selectedNodeInfo,
        treeType,
      });
    },
    [
      browserNodes,
      dataConnectionId,
      dispatch,
      handleNodeClick,
      handleNodeCollapse,
      handleNodeExpand,
      multiDatabase,
      nodes,
      pinnedOnly,
      selectedNode,
      selectedNodeInfo,
      treeType,
    ],
  );
}

export function useProjectHandleSchemaTreeKeyboardEvent({
  connectionImported,
  connectionType,
  dataConnectionId,
  handleNodeClick,
  handleNodeCollapse,
  handleNodeExpand,
  multiDatabase,
  pinnedOnly,
  treeType,
}: UseProjectHandleSchemaBrowserKeyboardEventArgs): (
  evt: React.KeyboardEvent,
) => void {
  const browserNodes = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]?.browserNodes ??
      stableEmptyArray(),
  );
  const selectedBrowserNodeInfo = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]
        ?.selectedBrowserNode,
  );
  const searchNodes = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]?.searchNodes ??
      stableEmptyArray(),
  );
  const selectedSearchNodeInfo = useSelector(
    (state) =>
      state.schemaTreeBrowser.dataConnections[dataConnectionId]
        ?.selectedSearchNode,
  );
  const selectedNodeInfo =
    treeType === "browser" ? selectedBrowserNodeInfo : selectedSearchNodeInfo;
  const nodes = treeType === "browser" ? browserNodes : searchNodes;
  const selectedNode =
    selectedNodeInfo?.path && nodes.length > 0
      ? Tree.nodeFromPath(selectedNodeInfo.path, nodes)
      : undefined;
  const dispatch = useDispatch();

  const { createSqlCell } = useTableProjectActions({
    connectionId: dataConnectionId,
    connectionImported,
    connectionType,
    dataSourceTableId: (selectedNodeInfo?.id ?? "") as DataSourceTableId,
    multiDatabase,
    insertLocation: { type: "global" },
  });

  return useCallback(
    (evt: React.KeyboardEvent) => {
      if (nodes.length === 0) return;
      if (evt.defaultPrevented) return;
      if (evt.key === Keys.ENTER && evtModKey(evt)) {
        // prevent propagating to the global key handler for running cells
        evt.stopPropagation();
        if (selectedNodeInfo?.type === "table") {
          void createSqlCell();
        }
      } else {
        handleSharedSchemaTreeKeyboardEvent({
          browserNodes,
          dataConnectionId,
          dispatch,
          evt,
          handleNodeClick,
          handleNodeCollapse,
          handleNodeExpand,
          multiDatabase,
          nodes,
          pinnedOnly,
          selectedNode,
          selectedNodeInfo,
          treeType,
        });
      }
    },
    [
      browserNodes,
      createSqlCell,
      dataConnectionId,
      dispatch,
      handleNodeClick,
      handleNodeCollapse,
      handleNodeExpand,
      multiDatabase,
      nodes,
      pinnedOnly,
      selectedNode,
      selectedNodeInfo,
      treeType,
    ],
  );
}

interface UseTableActionsArgs {
  connectionId: DataConnectionId;
  dataSourceTableId: DataSourceTableId;
  multiDatabase: boolean;
  connectionType: DataConnectionType;
  focusSearchBar?: () => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- please infer
export function useTableProjectActions(
  props: Parameters<typeof useTableAddToProjectActions>[0],
) {
  return {
    ...useTableUniversalActions(props),
    ...useTableAddToProjectActions(props),
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- please infer
export function useTableUniversalActions({
  connectionId,
  connectionType,
  dataSourceTableId,
  focusSearchBar,
  multiDatabase,
}: UseTableActionsArgs) {
  const dispatch = useDispatch();
  const toaster = useToaster();

  const client = useApolloClient();

  const getTable = useCallback(async () => {
    const fetchedData = (
      await client.query<
        DataSourceTableByIdQuery,
        DataSourceTableByIdQueryVariables
      >({
        query: DataSourceTableByIdDocument,
        variables: { dataSourceTableId },
        fetchPolicy: "cache-first",
      })
    ).data;
    return fetchedData.dataSourceTableById;
  }, [client, dataSourceTableId]);

  const getTablePath = useCallback(async () => {
    const table = await getTable();
    return getTablePathUtil({
      databaseName: table.dataSourceSchema.dataSourceDatabase.name,
      includeDatabaseName: multiDatabase,
      schemaName: table.dataSourceSchema.name,
      tableName: table.name,
    });
  }, [getTable, multiDatabase]);

  const copyTableNameToClipboard = useCallback(async () => {
    const tablePath = await getTablePath();
    if (tablePath == null) {
      toaster.show({
        message: "Could not copy table name",
        intent: "warning",
      });
      return;
    }
    const sql = generateTableNameSQL({
      dataConnectionType: connectionType,
      tablePath,
    });
    await navigator.clipboard.writeText(sql);
    toaster.show({ message: "Table name copied to clipboard" });
  }, [connectionType, getTablePath, toaster]);

  const copyAllColumnsToClipboard = useCallback(async () => {
    const table = await getTable();
    const tablePath = await getTablePath();
    if (table == null || tablePath == null) {
      toaster.show({ message: "Could not copy columns", intent: "warning" });
      return;
    }
    const sql = generateSelectColumnsSQL({
      dataConnectionType: connectionType,
      tablePath,
      columnNames: table.columns.map((c) => c.name),
    });
    await navigator.clipboard.writeText(sql);
    toaster.show({ message: "Columns copied to clipboard" });
  }, [connectionType, getTable, getTablePath, toaster]);

  const setTab = useSessionStorageSetter(
    SessionStorageKeys.DATA_BROWSER_VIEW(),
  );
  const setSchemaBrowserConnection = useLocalStorageSetter(
    LocalStorageKeys.SCHEMA_BROWSER_CONNECTION(),
  );
  const searchWithin = useCallback(async () => {
    const tablePath = await getTablePath();
    const searchInput = constructTableSearchQuery({ tablePath });
    dispatch(
      setSearchInput({
        dataConnectionId: connectionId,
        searchInput,
      }),
    );
    dispatch(
      setSearchActive({
        dataConnectionId: connectionId,
        searchActive: true,
      }),
    );
    setTab(DataBrowserTab.DATA);
    setSchemaBrowserConnection(connectionId);

    focusSearchBar?.();
  }, [
    connectionId,
    dispatch,
    focusSearchBar,
    getTablePath,
    setSchemaBrowserConnection,
    setTab,
  ]);

  return {
    searchWithin,
    copyAllColumnsToClipboard,
    copyTableNameToClipboard,
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- please infer
export function useTableAddToProjectActions({
  connectionId,
  connectionImported,
  connectionType,
  dataSourceTableId,
  focusSearchBar,
  insertLocation,
  multiDatabase,
  onCreateCell,
}: UseTableActionsArgs & {
  connectionImported: boolean;
  insertLocation: CellLocation | (() => CellLocation);
  onCreateCell?: (cellType: NoCodeCellType | typeof CellType.SQL) => void;
}) {
  const { dispatchAO } = useHexVersionAOContext();
  const dispatch = useDispatch();
  const toaster = useToaster();

  const client = useApolloClient();

  const {
    createChartCell: createChartCell_,
    createDisplayTableCell: createDisplayTableCell_,
    createExploreCell: createExploreCell_,
    createFilterCell: createFilterCell_,
    createPivotCell: createPivotCell_,
    insertCell,
  } = useCreateNoCodeCell({
    insertLocation,
    runAfterInsertion: true,
    onCreateCell,
  });

  const importConnection = useCallback(async () => {
    await dispatchAO(
      CREATE_DATA_CONNECTION_HEX_VERSION_LINK.create({
        dataConnectionHexVersionLinkId:
          uuid() as DataConnectionHexVersionLinkId,
        dataConnectionId: connectionId,
      }),
    ).promise;
  }, [connectionId, dispatchAO]);

  const getTable = useCallback(async () => {
    const fetchedData = (
      await client.query<
        DataSourceTableByIdQuery,
        DataSourceTableByIdQueryVariables
      >({
        query: DataSourceTableByIdDocument,
        variables: { dataSourceTableId },
        fetchPolicy: "cache-first",
      })
    ).data;
    return fetchedData.dataSourceTableById;
  }, [client, dataSourceTableId]);

  const getTablePath = useCallback(async () => {
    const table = await getTable();
    return getTablePathUtil({
      databaseName: table.dataSourceSchema.dataSourceDatabase.name,
      includeDatabaseName: multiDatabase,
      schemaName: table.dataSourceSchema.name,
      tableName: table.name,
    });
  }, [getTable, multiDatabase]);

  const copyTableNameToClipboard = useCallback(async () => {
    const tablePath = await getTablePath();
    if (tablePath == null) {
      toaster.show({
        message: "Could not copy table name",
        intent: "warning",
      });
      return;
    }
    const sql = generateTableNameSQL({
      dataConnectionType: connectionType,
      tablePath,
    });
    await navigator.clipboard.writeText(sql);
    toaster.show({ message: "Table name copied to clipboard" });
  }, [connectionType, getTablePath, toaster]);

  const copyAllColumnsToClipboard = useCallback(async () => {
    const table = await getTable();
    const tablePath = await getTablePath();
    if (table == null || tablePath == null) {
      toaster.show({ message: "Could not copy columns", intent: "warning" });
      return;
    }
    const sql = generateSelectColumnsSQL({
      dataConnectionType: connectionType,
      tablePath,
      columnNames: table.columns.map((c) => c.name),
    });
    await navigator.clipboard.writeText(sql);
    toaster.show({ message: "Columns copied to clipboard" });
  }, [connectionType, getTable, getTablePath, toaster]);

  const getDataSourceTableConfig = useCallback(
    async (cellType: NoCodeCellType): Promise<DataSourceTableConfig | null> => {
      const tablePath = await getTablePath();
      if (tablePath == null) {
        toaster.show({
          message: `Could not create ${cellType.toLowerCase()} cell`,
          intent: "warning",
        });
        return null;
      }
      if (!connectionImported) {
        await importConnection();
      }

      const dataSourceTableMetadata = {
        pathToTable: generateTableNameSQL({
          dataConnectionType: connectionType,
          tablePath,
        }),
        dataSourceTableId,
        connectionId,
      };
      return {
        ...dataSourceTableMetadata,
        dataframeName: generateDataframeNameForDataSourceTable(
          dataSourceTableMetadata,
        ),
      };
    },
    [
      connectionId,
      connectionImported,
      connectionType,
      dataSourceTableId,
      getTablePath,
      importConnection,
      toaster,
    ],
  );

  const getNewSqlCellFields = useGetNewSqlCellFields();

  const createSqlCell = useCallback(async () => {
    const table = await getTable();
    const tablePath = await getTablePath();
    if (tablePath == null) {
      toaster.show({
        message: "Could not create SQL cell",
        intent: "warning",
      });
      return;
    }
    if (!connectionImported) {
      await importConnection();
    }
    const sql = generateTableNameSQL({
      dataConnectionType: connectionType,
      tablePath,
    });

    let columns: Array<{ name: string }> | undefined = undefined;
    if (table.tableType === TableType.METRIC_VIEW) {
      columns = table.columns
        .filter(
          (c) => c.columnSemanticLayerType !== ColumnSemanticLayerType.MEASURE,
        )
        .map((c) => ({ name: c.name }));
    }

    const { castDecimals, resultVariable } = getNewSqlCellFields();
    insertCell(
      createSqlCellPayload({
        resultVariable,
        connectionId,
        castDecimals,
        source: buildLimitedQuery({
          connectionType,
          columns,
          tablePath: sql,
        }),
      }),
    );
    onCreateCell?.(CellType.SQL);
  }, [
    getTable,
    getTablePath,
    connectionImported,
    connectionType,
    getNewSqlCellFields,
    insertCell,
    connectionId,
    onCreateCell,
    toaster,
    importConnection,
  ]);
  const createAsExplore = useHexFlag("explore-backed-charts");
  const createChartCell = useCallback(async () => {
    const dataSourceTableConfig = await getDataSourceTableConfig(
      CellType.CHART,
    );
    if (!dataSourceTableConfig) {
      return;
    }
    createChartCell_(dataSourceTableConfig, createAsExplore);
    onCreateCell?.(createAsExplore ? CellType.EXPLORE : CellType.CHART);
  }, [
    createAsExplore,
    createChartCell_,
    getDataSourceTableConfig,
    onCreateCell,
  ]);
  const exploreBackedPivots = useHexFlag("explore-backed-pivots");
  const createPivotCell = useCallback(async () => {
    const dataSourceTableConfig = await getDataSourceTableConfig(
      CellType.PIVOT,
    );
    if (dataSourceTableConfig) {
      createPivotCell_(dataSourceTableConfig, exploreBackedPivots);
    }
  }, [createPivotCell_, getDataSourceTableConfig, exploreBackedPivots]);

  const createFilterCell = useCallback(async () => {
    const dataSourceTableConfig = await getDataSourceTableConfig(
      CellType.FILTER,
    );
    if (dataSourceTableConfig) {
      createFilterCell_(dataSourceTableConfig, true);
      onCreateCell?.(CellType.FILTER);
    }
  }, [createFilterCell_, getDataSourceTableConfig, onCreateCell]);

  const createDisplayTableCell = useCallback(async () => {
    const dataSourceTableConfig = await getDataSourceTableConfig(
      CellType.DISPLAY_TABLE,
    );
    if (dataSourceTableConfig) {
      createDisplayTableCell_(dataSourceTableConfig);
      onCreateCell?.(CellType.DISPLAY_TABLE);
    }
  }, [createDisplayTableCell_, getDataSourceTableConfig, onCreateCell]);

  const createExploreCell = useCallback(async () => {
    const dataSourceTableConfig = await getDataSourceTableConfig(
      CellType.EXPLORE,
    );
    if (dataSourceTableConfig) {
      createExploreCell_(dataSourceTableConfig);
      onCreateCell?.(CellType.EXPLORE);
    }
  }, [createExploreCell_, getDataSourceTableConfig, onCreateCell]);

  const setTab = useSessionStorageSetter(
    SessionStorageKeys.DATA_BROWSER_VIEW(),
  );

  const setSchemaBrowserConnection = useLocalStorageSetter(
    LocalStorageKeys.SCHEMA_BROWSER_CONNECTION(),
  );

  const searchWithin = useCallback(async () => {
    const tablePath = await getTablePath();
    if (tablePath) {
      dispatch(
        setSearchInput({
          dataConnectionId: connectionId,
          searchInput: `table:"${tablePath.join(".")}" `,
        }),
      );
    }
    dispatch(
      setSearchActive({
        dataConnectionId: connectionId,
        searchActive: true,
      }),
    );
    setTab(DataBrowserTab.DATA);
    setSchemaBrowserConnection(connectionId);

    focusSearchBar?.();
  }, [
    connectionId,
    dispatch,
    focusSearchBar,
    getTablePath,
    setSchemaBrowserConnection,
    setTab,
  ]);

  return {
    searchWithin,
    createSqlCell,
    copyAllColumnsToClipboard,
    copyTableNameToClipboard,
    createChartCell,
    createPivotCell,
    createFilterCell,
    createDisplayTableCell,
    createExploreCell,
  };
}

export const allNodesCollapsed = (nodes: TreeNodeInfo[]): boolean => {
  return nodes.every((node) => {
    return (
      !node.isExpanded &&
      (node.childNodes ? allNodesCollapsed(node.childNodes) : true)
    );
  });
};

export function getTableIdPath(
  table: TableListFragment,
  multiDatabase: boolean,
): string[] {
  return multiDatabase
    ? [
        table.dataSourceSchema.dataSourceDatabase.id,
        table.dataSourceSchema.id,
        table.id,
      ]
    : [table.dataSourceSchema.id, table.id];
}

export function getSchemaIdPath(
  schema: PinnedSchemaFragment,
  multiDatabase: boolean,
): string[] {
  return multiDatabase
    ? [schema.dataSourceDatabase.id, schema.id]
    : [schema.id];
}
